import { ASPECT_RATIO } from "@circle-react/components/constants";
import { Modal } from "@circleco/compass/components/Modal";

import { t } from "@/i18n-js/instance";

import { aspectRatioLabels } from "../constants";
import type { ImageEditorModalWithCropProps } from "../index";
import { FileUploadMethods } from "./FileUploadMethods";
import { ImageCropper } from "./ImageCropper";
import { useImageEditor } from "./useImageEditor";

export const ImageEditorModalWithCropComponent = ({
  aspectRatio = ASPECT_RATIO.DEFAULT,
  attachImage = null,
  closeModal,
  customAspectRatioHelpText = "",
  formClass = "",
  hasFile = false,
  initialFile = null,
  hideEmbedUrl = false,
  hideUnsplash = false,
  inputFieldName = "",
  isCropEnabled = false,
  isPublic = false,
  setIsLoading = () => false,
  saveImage = null,
  setHasFile,
  showModal = false,
  size = "lg",
  title = "",
  uploadProgress,
}: ImageEditorModalWithCropProps) => {
  const {
    state,
    addUnsplash,
    clearError,
    insertFiles,
    onChangeEmbedUrl,
    addEmbed,
    onCropComplete,
    saveCroppedImage,
    removeImage,
    setCrop,
    setZoom,
  } = useImageEditor({
    attachImage,
    closeModal,
    formClass,
    initialFile,
    inputFieldName,
    isCropEnabled,
    isPublic,
    saveImage,
    setHasFile,
    setIsLoading,
    showModal,
    uploadProgress,
  });

  const shouldShowImageCropper = hasFile && isCropEnabled;

  const aspectRatioHelpText =
    customAspectRatioHelpText === null
      ? ""
      : (customAspectRatioHelpText ?? aspectRatioLabels[aspectRatio] ?? "");

  return (
    <Modal.Root
      open={showModal}
      onOpenChange={open => {
        if (!open) closeModal();
      }}
      size={size}
    >
      <Modal.Content>
        <Modal.Header title={title} />
        <Modal.Body>
          {shouldShowImageCropper && (
            <ImageCropper
              file={state.stringFile}
              crop={state.crop}
              onCropComplete={onCropComplete}
              aspectRatio={aspectRatio}
              zoom={state.zoom}
              setZoom={setZoom}
              setCrop={setCrop}
              imageError={state.imageError}
            />
          )}
          {!hasFile && (
            <FileUploadMethods
              clearError={clearError}
              hideUnsplash={hideUnsplash}
              hideEmbedUrl={hideEmbedUrl}
              addUnsplash={addUnsplash}
              imageError={state.imageError}
              insertFiles={insertFiles}
              aspectRatioHelpText={aspectRatioHelpText}
              embedUrl={state.embedUrl}
              onChangeEmbedUrl={onChangeEmbedUrl}
              addEmbed={addEmbed}
            />
          )}
        </Modal.Body>
        {isCropEnabled && (
          <Modal.Footer
            secondaryAction={{
              label: t("cancel"),
              onClick: removeImage,
            }}
            primaryAction={{
              label: t("save_changes"),
              disabled: !hasFile,
              onClick: () => {
                void saveCroppedImage();
              },
            }}
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
